import React from "react";

import { Button } from "../../../../components/ui/Button";
import { ProgressBar } from "primereact/progressbar";

import { useSelector, useDispatch } from "react-redux";
import { useProfileContext } from "../../../Contexts/ProfileContext";
import { toggleDialog } from "../../../../redux/Slices/Global/globalSlice";
import { useTranslation } from "react-i18next";

function formatNumber(n) {
    return new Intl.NumberFormat().format(n ?? 0);
}
function formatBytes(bytes) {
    const b = Number(bytes || 0);
    if (!b) return "0 B";
    const u = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(b) / Math.log(1024));
    const val = b / Math.pow(1024, i);
    return `${val % 1 === 0 ? val : val.toFixed(1)} ${u[i]}`;
}

export default function UsageDetailCard() {
    const { t } = useTranslation();
    const { companyDetails } = useSelector((s) => s.global);
    const dispatch = useDispatch();
    const { setAction } = useProfileContext();

    const c = companyDetails?.company ?? {};
    const usedDevices = Number(companyDetails?.total_used_devices ?? 0);
    const allowedDevices = Number(c.allowed_device_number ?? 0);

    const usedMediaBytes = Number(companyDetails?.media_usage ?? c.total_media_usage ?? 0);
    const allowedMediaBytes = Number(c.allowed_media_usage ?? 0);

    const devicePct = allowedDevices ? Math.round((usedDevices / allowedDevices) * 100) : 0;
    const mediaPct = allowedMediaBytes ? Math.round((usedMediaBytes / allowedMediaBytes) * 100) : 0;

    const remainingDevices = Math.max(allowedDevices - usedDevices, 0);
    const remainingMedia = Math.max(allowedMediaBytes - usedMediaBytes, 0);
    const addScreens = () => {
        setAction("addScreens")
        dispatch(toggleDialog("addScreens"))
    }
    return (
        <div className="w-full border rounded-3xl bg-white p-4 md:p-5 flex flex-col h-auto md:h-[355px]">
            {/* Header (wraps on small screens) */}
            <div className="flex flex-wrap items-start md:items-center justify-between gap-2 min-h-[56px]">
                <h3 className="text-lg md:text-[18px] font-semibold text-slate-700">
                    {t('billing.usage_summary')}
                </h3>
                {/* (No "add screens" purchase button: packages are granted by the PopupPush team.) */}
            </div>

            <hr className="w-full my-3 md:my-4" />

            {/* Content area — fills leftover height; scrolls on md+ to keep card height exact */}
            <div className="flex-1 min-h-0 grid grid-cols-1 gap-4 md:gap-6 md:overflow-auto md:pr-1 content-start">
                {/* Devices */}
                <section className="w-full">
                    <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold whitespace-nowrap tabular-nums">
                            {formatNumber(usedDevices)} / {formatNumber(allowedDevices)}
                        </span>
                        <span className="text-sm text-slate-500 whitespace-nowrap tabular-nums">
                            {devicePct}%
                        </span>
                    </div>

                    <ProgressBar
                        value={devicePct}
                        showValue={false}
                        className="usage-bar usage-bar--devices"
                    />

                    <div className="mt-2 text-sm text-slate-500 flex items-center justify-between">
                        <span>{t('billing.devices_used')}</span>
                        <span className="text-slate-400 whitespace-nowrap">
                            {t('billing.remaining')}: {formatNumber(remainingDevices)}
                        </span>
                    </div>
                </section>

                {/* Storage */}
                <section className="w-full">
                    <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold whitespace-nowrap tabular-nums">
                            {formatBytes(usedMediaBytes)} / {formatBytes(allowedMediaBytes)}
                        </span>
                        <span className="text-sm text-slate-500 whitespace-nowrap tabular-nums">
                            {mediaPct}%
                        </span>
                    </div>

                    <ProgressBar
                        value={mediaPct}
                        showValue={false}
                        className="usage-bar usage-bar--storage"
                    />

                    <div className="mt-2 text-sm text-slate-500 flex items-center justify-between">
                        <span>{t('billing.storage_used')}</span>
                        <span className="text-slate-400 whitespace-nowrap">
                            {t('billing.remaining')}: {formatBytes(remainingMedia)}
                        </span>
                    </div>
                </section>
            </div>

            {/* Local styles for thick, rounded progressbars (keeps original look) */}
            <style>{`
        .usage-bar {
          height: 12px;
          border-radius: 9999px;
          background: #eef2f6; /* track */
        }
        .usage-bar .p-progressbar-value {
          border-radius: 9999px;
        }
        .usage-bar--devices .p-progressbar-value {
          background: linear-gradient(90deg, #7c8cfb, #8ea0ff);
        }
        .usage-bar--storage .p-progressbar-value {
          background: linear-gradient(90deg, #cfd8e3, #bcc7d6);
        }
      `}</style>
        </div>
    );
}
